import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../styles/AdminReturns.css";

const STATUS_OPTIONS = [
  "ALL",
  "REQUESTED",
  "APPROVED",
  "AWAITING_SHIPMENT",
  "RECEIVED",
  "REFUND_PENDING",
  "REFUNDED",
  "REJECTED",
  "CANCELLED",
];

export default function AdminReturns() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [returns, setReturns] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadReturns();
  }, []);

  const loadReturns = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/admin/returns", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("RETURNS_LOAD_FAILED");
      }

      const data = await response.json();

      setReturns(Array.isArray(data) ? data : []);
    } catch {
      setError(t("adminReturns.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const filteredReturns = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return returns.filter((returnRequest) => {
      const matchesSearch =
        normalizedSearch === "" ||
        returnRequest.returnNumber
          ?.toLowerCase()
          .includes(normalizedSearch) ||
        returnRequest.orderNumber
          ?.toLowerCase()
          .includes(normalizedSearch) ||
        returnRequest.reason
          ?.toLowerCase()
          .includes(normalizedSearch);

      const matchesStatus =
        statusFilter === "ALL" ||
        returnRequest.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [returns, search, statusFilter]);

  const formatDate = (date) => {
    if (!date) {
      return "—";
    }

    return new Date(date).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(2);
  };

  return (
    <main className="admin-returns-page">
      <section className="admin-returns-container">
        <button
          type="button"
          className="admin-returns-back"
          onClick={() => navigate("/admin")}
        >
          ← {t("adminReturns.backToDashboard")}
        </button>

        <div className="admin-returns-heading">
          <div>
            <h1>{t("adminReturns.title")}</h1>
            <p>{t("adminReturns.subtitle")}</p>
          </div>

          <button
            type="button"
            className="admin-returns-refresh"
            onClick={loadReturns}
            disabled={loading}
          >
            {loading
              ? t("adminReturns.loading")
              : t("adminReturns.refresh")}
          </button>
        </div>

        <div className="admin-returns-stats">
          <article>
            <span>{t("adminReturns.totalReturns")}</span>
            <strong>{returns.length}</strong>
          </article>

          <article>
            <span>{t("adminReturns.pendingReview")}</span>
            <strong>
              {
                returns.filter(
                  (returnRequest) =>
                    returnRequest.status === "REQUESTED"
                ).length
              }
            </strong>
          </article>

          <article>
            <span>{t("adminReturns.awaitingProducts")}</span>
            <strong>
              {
                returns.filter((returnRequest) =>
                  [
                    "APPROVED",
                    "AWAITING_SHIPMENT",
                  ].includes(returnRequest.status)
                ).length
              }
            </strong>
          </article>

          <article>
            <span>{t("adminReturns.pendingRefund")}</span>
            <strong>
              {
                returns.filter(
                  (returnRequest) =>
                    returnRequest.status ===
                    "REFUND_PENDING"
                ).length
              }
            </strong>
          </article>
        </div>

        <div className="admin-returns-filters">
          <input
            type="search"
            value={search}
            placeholder={t(
              "adminReturns.searchPlaceholder"
            )}
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status === "ALL"
                  ? t("adminReturns.allStatuses")
                  : t(`returns.status.${status}`)}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="admin-returns-error">
            <p>{error}</p>

            <button
              type="button"
              onClick={loadReturns}
            >
              {t("adminReturns.retry")}
            </button>
          </div>
        )}

        {!error && loading && (
          <div className="admin-returns-loading">
            {t("adminReturns.loading")}
          </div>
        )}

        {!error && !loading && (
          <div className="admin-returns-table">
            <div className="admin-returns-header">
              <span>{t("adminReturns.returnNumber")}</span>
              <span>{t("adminReturns.order")}</span>
              <span>{t("adminReturns.requestDate")}</span>
              <span>{t("adminReturns.reason")}</span>
              <span>{t("adminReturns.status")}</span>
              <span>{t("adminReturns.amount")}</span>
              <span></span>
            </div>

            {filteredReturns.length === 0 ? (
              <div className="admin-returns-empty">
                <h2>{t("adminReturns.noReturnsTitle")}</h2>
                <p>{t("adminReturns.noReturnsText")}</p>
              </div>
            ) : (
              filteredReturns.map((returnRequest) => (
                <article
                  className="admin-returns-row"
                  key={returnRequest.id}
                >
                  <span className="admin-return-number">
                    {returnRequest.returnNumber}
                  </span>

                  <span>
                    {returnRequest.orderNumber}
                  </span>

                  <span>
                    {formatDate(
                      returnRequest.requestedAt
                    )}
                  </span>

                  <span className="admin-return-reason">
                    {returnRequest.reason
                      ? t(
                          `returns.reasons.${returnRequest.reason}`,
                          {
                            defaultValue:
                              returnRequest.reason,
                          }
                        )
                      : "—"}
                  </span>

                  <span
                    className={`admin-return-status ${returnRequest.status
                      ?.toLowerCase()
                      .replaceAll("_", "-")}`}
                  >
                    {t(
                      `returns.status.${returnRequest.status}`
                    )}
                  </span>

                  <strong>
                    {formatAmount(
                      returnRequest.refundAmount
                    )}{" "}
                    €
                  </strong>

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/admin/returns/${returnRequest.id}`
                      )
                    }
                  >
                    {t("adminReturns.viewDetail")}
                  </button>
                </article>
              ))
            )}
          </div>
        )}
      </section>
    </main>
  );
}